import { useState, useEffect } from "react";

export type UserPreferences = {
  avatarColor: string;
  avatarEmoji: string;
  bannerColor: string;
  presence: "online" | "invisible" | "dnd";
};

// Default preferences - matching original Material Design theme
const BANNER_COLOR_LIGHT = "#B0BEC5"; // Blue-gray-300 — readable with dark text
const BANNER_COLOR_DARK  = "#263238"; // Blue-gray-900 — readable with light text

function getDefaultBannerColor(): string {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? BANNER_COLOR_DARK
    : BANNER_COLOR_LIGHT;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  avatarColor: "#546E7A", // Blue-gray-600 (matches original bg-tertiary)
  avatarEmoji: "",
  bannerColor: getDefaultBannerColor(),
  presence: "online",
};

export function useUserPreferences(): UserPreferences {
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    // Load preferences from localStorage
    const loadPreferences = () => {
      try {
        const avatarColor = localStorage.getItem("moby_pref_avatarColor");
        const avatarEmoji = localStorage.getItem("moby_pref_avatarEmoji");
        const bannerColor = localStorage.getItem("moby_pref_bannerColor");
        const presence = localStorage.getItem("moby_pref_presence");

        setPreferences({
          avatarColor: avatarColor ? JSON.parse(avatarColor) : DEFAULT_PREFERENCES.avatarColor,
          avatarEmoji: avatarEmoji ? JSON.parse(avatarEmoji) : DEFAULT_PREFERENCES.avatarEmoji,
          bannerColor: bannerColor ? JSON.parse(bannerColor) : getDefaultBannerColor(),
          presence: presence ? JSON.parse(presence) : DEFAULT_PREFERENCES.presence,
        });
      } catch (error) {
        console.error("Failed to load user preferences:", error);
        setPreferences(DEFAULT_PREFERENCES);
      }
    };

    loadPreferences();

    // Listen for preference updates
    const handlePreferencesUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setPreferences((prev) => ({
          ...prev,
          ...customEvent.detail,
        }));
      } else {
        loadPreferences();
      }
    };

    window.addEventListener("user-preferences-updated", handlePreferencesUpdate);

    // React to OS color-scheme changes so the default banner adapts
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    const handleSchemeChange = () => {
      // Only update if user hasn't set a custom banner color
      if (!localStorage.getItem("moby_pref_bannerColor")) {
        setPreferences((prev) => ({ ...prev, bannerColor: getDefaultBannerColor() }));
      }
    };
    mql?.addEventListener?.("change", handleSchemeChange);

    return () => {
      window.removeEventListener("user-preferences-updated", handlePreferencesUpdate);
      mql?.removeEventListener?.("change", handleSchemeChange);
    };
  }, []);

  return preferences;
}
